import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSmsCompliance } from '../../../../store/trustHubSlice';
import A2PBrandCard from '../components/sms/A2PBrandCard';
import A2PCampaignCard from '../components/sms/A2PCampaignCard';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import './SmsComplianceTab.css';

const SmsComplianceTab = ({ user }) => {
  const dispatch = useDispatch();
  const { smsCompliance, smsLoading, smsError } = useSelector(
    (state) => state.trustHub
  );

  const userId = user?.user_id || user?.id;
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    if (userId) {
      console.log('SmsComplianceTab - Fetching SMS compliance for user:', userId);
      dispatch(fetchSmsCompliance(userId));
    }
  }, [dispatch, userId]);

  // Handle loading state
  if (smsLoading && !smsCompliance.brand && !smsCompliance.campaign) {
    return (
      <div className="sms-compliance-tab">
        <LoadingSkeleton variant="dashboard" />
      </div>
    );
  }

  // Handle error state
  if (smsError) {
    return (
      <div className="sms-compliance-tab">
        <div className="alert alert-danger">
          <h4>Error Loading SMS Compliance</h4>
          <p>{smsError}</p>
          <button
            className="btn linear-gradient zy-save-btn ml-2 mt-3"
            onClick={() => userId && dispatch(fetchSmsCompliance(userId))}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Refresh data after updates
  const handleRefresh = () => {
    if (userId) {
      dispatch(fetchSmsCompliance(userId));
    }
  };

  // Check if Brand is approved - Campaign requires this
  const isBrandApproved = smsCompliance?.brand?.status &&
    ['approved', 'twilio-approved', 'verified', 'VERIFIED'].includes(smsCompliance.brand.status);

  // Check if Campaign is approved - Phone assignment requires this
  const isCampaignApproved = smsCompliance?.campaign?.status &&
    ['approved', 'twilio-approved', 'verified', 'VERIFIED'].includes(smsCompliance.campaign.status);

  // Check if at least one phone is assigned
  const hasAssignedPhones = smsCompliance?.phoneAssignments?.hasAssigned || false;

  return (
    <div className="sms-compliance-tab">
      {/* Header */}
      <div className="sms-compliance-header">
        <div>
          <h2>
            SMS Compliance (A2P 10DLC)
            <button
              className="info-icon-btn"
              onClick={() => setShowInfoModal(true)}
              title="View A2P 10DLC Information"
              style={{
                marginLeft: '12px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '2px solid #087ab6',
                background: 'transparent',
                color: '#087ab6',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                padding: '10px 12px'
              }}
              
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#087ab6';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#087ab6';
              }}
              
            >
              i
            </button>
          </h2>
          <p className="subtitle">
            Register your brand first, then create campaigns for SMS messaging compliance with A2P 10DLC regulations
          </p>
        </div>
      </div>

      {/* Registration Flow Info */}
      <div className="registration-flow">
        <div className={`flow-step ${smsCompliance?.brand ? 'flow-step--completed' : 'flow-step--active'}`}>
          <span className="flow-number">1</span>
          <div className="flow-content">
            <h4>Register Brand</h4>
            <p>Create and verify your business brand</p>
            {!isBrandApproved && <span className="flow-status">Required First</span>}
          </div>
        </div>
        <div className="flow-arrow">→</div>
        <div className={`flow-step ${!isBrandApproved ? 'flow-step--locked' : isCampaignApproved ? 'flow-step--completed' : 'flow-step--active'}`}>
          <span className="flow-number">2</span>
          <div className="flow-content">
            <h4>Create Campaign</h4>
            <p>Set up your SMS campaign use case</p>
            {!isBrandApproved && <span className="flow-status">Brand Required</span>}
          </div>
        </div>
        <div className="flow-arrow">→</div>
        <div className={`flow-step ${!isCampaignApproved ? 'flow-step--locked' : hasAssignedPhones ? 'flow-step--completed' : 'flow-step--active'}`}>
          <span className="flow-number">3</span>
          <div className="flow-content">
            <h4>Assign Numbers</h4>
            <p>Link phone numbers to campaign</p>
            {!isCampaignApproved && <span className="flow-status">Campaign Required</span>}
          </div>
        </div>
      </div>

      {/* Compliance Cards Grid */}
      <div className="sms-compliance-grid">
        {/* A2P Brand Card - Must be completed first */}
        <A2PBrandCard
          userId={userId}
          brandData={smsCompliance.brand}
          onUpdate={handleRefresh}
        />

        {/* A2P Campaign Card - Requires Brand approval */}
        <A2PCampaignCard
          userId={userId}
          campaignData={smsCompliance.campaign}
          brandData={smsCompliance.brand}
          onUpdate={handleRefresh}
          disabled={!isBrandApproved}
        />
      </div>

      {/* A2P Information Modal */}
      {showInfoModal && (
        <div className="sms-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="sms-modal-content sms-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="sms-modal-header">
              <h3>A2P 10DLC Registration Information</h3>
              <button className="sms-modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            </div>

            <div className="sms-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Requirements Section */}
              <div className="form-section">
                <h4>Registration Requirements</h4>
                <div style={{ marginTop: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Requirement</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>Low-Volume Standard</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>Standard</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 500 }}>You have Business Registration ID</div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Such as a US EIN</div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ color: '#10b981', fontSize: '20px' }}>✓</span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ color: '#10b981', fontSize: '20px' }}>✓</span>
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 500 }}>You are an individual and not acting as part of a registered business or organization</div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Includes hobbyists that do not file taxes as a business</div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ color: '#ef4444', fontSize: '20px' }}>✗</span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ color: '#ef4444', fontSize: '20px' }}>✗</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Messaging Capabilities */}
              <div className="form-section">
                <h4>Messaging Capabilities</h4>
                <div style={{ marginTop: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Capability</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>Low-Volume Standard</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>Standard</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 500 }}>Max daily message volume</div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>~6,000 segments per day</span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>~6,000 segments per day</span>
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 500 }}>Max throughput</div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span>low</span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span>high</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Fees */}
              <div className="form-section">
                <h4>Fees</h4>
                <div style={{ marginTop: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Fee Type</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>Low-Volume Standard</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>Standard</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 500 }}>One-time Brand Registration</div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>$4.50</span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>$46</span>
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 500 }}>One-time Campaign Vetting Fee</div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Additional fees apply if Campaign is resubmitted upon initial failure</div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>$15 per Campaign</span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>$15 per Campaign</span>
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 500 }}>Monthly Campaign Fee</div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>$1.50 - $10 per Campaign</span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 500 }}>$1.50 - $10 per Campaign</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Information to Submit */}
              <div className="form-section">
                <h4>Information You Need to Submit</h4>
                <div style={{ marginTop: '16px' }}>
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <p style={{ fontWeight: 500, marginBottom: '12px' }}>Have this ready before you start</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      <li style={{ marginBottom: '8px' }}>Business information</li>
                      <li style={{ marginBottom: '8px' }}>Business registration ID such as US EIN</li>
                      <li style={{ marginBottom: '8px' }}>Contact information</li>
                      <li style={{ marginBottom: '8px' }}>Business reason to use messaging and sample messages</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div style={{ marginTop: '24px', padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}>
                  <strong>Note:</strong> Brand registration typically takes 10-15 business days for approval. Campaign approval may take an additional 5-7 business days.
                </p>
              </div>
            </div>

            <div className="sms-modal-footer">
              <button
                className="btn linear-gradient zy-save-btn"
                onClick={() => setShowInfoModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsComplianceTab;
